// Add or edit a case of the file plan: its index, title, the unit that keeps
// it and its retention class — picked from the active catalogue
// with a search, since the catalogue runs to hundreds of articles.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FieldError, REQUIRED_LABEL } from '@/shared/ui/FieldError.jsx';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { periodLabel, titleIn } from '../lib/retention.js';
import { LIMITS, caseProblems } from '../lib/validation.js';

const FIELDS = ['case_index', 'title', 'unit', 'note', 'retention_class_uuid'];

export function CaseDialog({ open, onOpenChange, initial, classes, onSave }) {
  const { t, i18n } = useTranslation('records');
  const [form, setForm] = useState({ case_index: '', title: '', unit: '', note: '', retention_class_uuid: null });
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const checks = useFieldErrors(caseProblems(form));
  const { errors, reset } = checks;

  useEffect(() => {
    if (!open) return;
    setForm({
      case_index: initial?.case_index || '',
      title: initial?.title || '',
      unit: initial?.unit || '',
      note: initial?.note || '',
      retention_class_uuid: initial?.retention_class_uuid ?? null,
    });
    setQ('');
    reset();
  }, [open, initial, reset]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return classes;
    return classes.filter((c) => [c.article, ...Object.values(c.title || {})].some((v) => (v || '').toLowerCase().includes(needle)));
  }, [classes, q]);

  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((key) => checks.touch(key));
  };

  const submit = async () => {
    if (!checks.attempt()) return;
    setBusy(true);
    try {
      const res = await onSave({
        case_index: form.case_index.trim(),
        title: form.title.trim(),
        unit: form.unit.trim() || null,
        note: form.note.trim() || null,
        retention_class_uuid: form.retention_class_uuid,
      });
      if (res && !res.success) checks.showServer(res, FIELDS);
    } finally {
      setBusy(false);
    }
  };
  const err = (field) => checks.describe(field, 'case');

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(initial ? 'case.editTitle' : 'case.addTitle')}</DialogTitle>
          <DialogDescription>{t('case.description')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-[9rem_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="case-index" className={REQUIRED_LABEL}>{t('case.index')}</Label>
              <Input id="case-index" required maxLength={LIMITS.code} value={form.case_index}
                onChange={(e) => set({ case_index: e.target.value })} placeholder="01-05" {...err('case_index')} />
              <FieldError id="case-case_index-error" message={errors.case_index} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="case-unit">{t('case.unit')}</Label>
              <Input id="case-unit" maxLength={LIMITS.unit} value={form.unit} onChange={(e) => set({ unit: e.target.value })} {...err('unit')} />
              <FieldError id="case-unit-error" message={errors.unit} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="case-title" className={REQUIRED_LABEL}>{t('case.title')}</Label>
            <Textarea id="case-title" rows={2} required maxLength={LIMITS.caseTitle} value={form.title}
              onChange={(e) => set({ title: e.target.value })} {...err('title')} />
            <FieldError id="case-title-error" message={errors.title} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="case-class-search" className={REQUIRED_LABEL}>{t('case.retention')}</Label>
            <Input id="case-class-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('case.searchClass')} />
            <ul role="listbox" aria-label={t('case.retention')} className="max-h-56 divide-y overflow-y-auto rounded-md border">
              {shown.length === 0 && <li className="px-3 py-3 text-center text-xs text-muted-foreground">{t('case.noClass')}</li>}
              {shown.map((c) => {
                const selected = form.retention_class_uuid === c.uuid;
                return (
                  <li
                    key={c.uuid}
                    role="option"
                    aria-selected={selected}
                    tabIndex={0}
                    onClick={() => set({ retention_class_uuid: c.uuid })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        set({ retention_class_uuid: c.uuid });
                      }
                    }}
                    className={cn('flex cursor-pointer items-start gap-3 px-3 py-2 text-sm hover:bg-muted', selected && 'bg-brand/10')}
                  >
                    <span className="w-14 shrink-0 font-mono text-xs text-muted-foreground">{c.article}</span>
                    <span className="flex-1">{titleIn(c.title, i18n.language)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{periodLabel(c, t)}</span>
                  </li>
                );
              })}
            </ul>
            <FieldError id="case-retention_class_uuid-error" message={errors.retention_class_uuid} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="case-note">{t('case.note')}</Label>
            <Input id="case-note" maxLength={LIMITS.note} value={form.note} onChange={(e) => set({ note: e.target.value })} {...err('note')} />
            <FieldError id="case-note-error" message={errors.note} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('actions.cancel')}</Button>
          <Button onClick={submit} disabled={busy || checks.blocked} data-testid="case-save">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
